/**
 * useChartView — domain-state viewport for native-SVG charts.
 *
 * The model: each chart maintains an `xDomain` and `yDomain` in DATA
 * space. Scales are built fresh from these on every render. Gestures
 * (rectangle zoom, double-click reset, preset selection, axis-edge drag)
 * mutate the domains directly.
 *
 * This is the post-d3-zoom architecture. d3-zoom's single-`k` transform
 * couples x and y scaling together, which means rectangle zoom (where
 * the user picks an arbitrary x range AND y range) can't honor both
 * dimensions exactly. Domain state has no such constraint — the new
 * domain is whatever the user drew.
 *
 * Gestures the hook owns directly:
 *   - Click-drag rectangle on the SVG body → zoom to that rect.
 *   - Double-click on the SVG body → reset to base domains.
 *
 * Gestures the hook does NOT own (caller wires separately):
 *   - Axis-edge drag (caller uses useAxisScale and calls setXDomain/setYDomain).
 *   - Preset-row time-range selection (ChartTimeRangeRow → setXDomain).
 *
 * The chart renders the live rectangle preview itself via the
 * `rectPreview` value during drag.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

export type Domain1D = readonly [number, number];

export interface UseChartViewOptions {
  /** Initial / "reset target" x-domain in data space (numeric — pass Date.getTime() for time). */
  baseXDomain: Domain1D;
  /** Initial / "reset target" y-domain in data space. */
  baseYDomain: Domain1D;
  /** Enable click-drag rectangle zoom. Default true. */
  rectangleZoom?: boolean;
  /** Enable double-click reset. Default true. */
  dblclickResets?: boolean;
  /** Minimum rectangle size in pixels — drags smaller than this are treated
   *  as accidental and ignored on release. Default 6. */
  minDragPx?: number;
  /**
   * Which axes the box-drag zoom affects by default.
   *   - 'x' (default for time-axis charts) → x-only zoom; preview rect spans
   *     full chart height. Matches Grafana / Datadog.
   *   - 'y' → y-only zoom; preview spans full chart width.
   *   - 'xy' → free 2D zoom; preview rect as-drawn.
   * Shift+drag always overrides to 'xy' regardless of this option.
   */
  dragZoomAxes?: 'x' | 'y' | 'xy';
  /**
   * Inner chart bounds in SVG pixels. Used to draw the full-height
   * (for 'x' mode) or full-width (for 'y' mode) preview rectangle.
   * If omitted, the preview falls back to the user's exact drag rect.
   */
  chartArea?: { top: number; bottom: number; left: number; right: number };
}

export interface RectPreview {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

export interface UseChartViewResult {
  xDomain: Domain1D;
  yDomain: Domain1D;
  /** Live rectangle in SVG pixel coords during drag. null when idle. */
  rectPreview: RectPreview | null;
  /** Set x-domain directly (preset row, axis drag). */
  setXDomain: (d: Domain1D) => void;
  /** Set y-domain directly. */
  setYDomain: (d: Domain1D) => void;
  /** Reset both to base. */
  reset: () => void;
  /** Has the user zoomed away from base in any way. */
  isZoomed: boolean;
}

function nearlyEqual(a: Domain1D, b: Domain1D, eps = 1e-9): boolean {
  return Math.abs(a[0] - b[0]) < eps && Math.abs(a[1] - b[1]) < eps;
}

/**
 * Wires rectangle-zoom + dblclick-reset on the SVG element and exposes
 * x/y domain state for the chart to read. Caller is responsible for
 * building scales from `xDomain` / `yDomain` and rendering the
 * `rectPreview` `<rect>` during drag.
 *
 * @param svgRef - SVG element ref (rect zoom + dblclick attach here).
 * @param xScalePxToData - convert local SVG x px → data x. Used during
 *        drag-release to convert rect pixel coords into new domain.
 * @param yScalePxToData - convert local SVG y px → data y.
 */
export function useChartView(
  svgRef: React.RefObject<SVGSVGElement>,
  xScalePxToData: (px: number) => number,
  yScalePxToData: (py: number) => number,
  options: UseChartViewOptions,
): UseChartViewResult {
  const {
    baseXDomain,
    baseYDomain,
    rectangleZoom = true,
    dblclickResets = true,
    minDragPx = 6,
    dragZoomAxes = 'xy',
    chartArea,
  } = options;

  // Stash mutable options in refs so the gesture handlers (set up once)
  // read the latest configured values without forcing handler re-binds.
  const dragZoomAxesRef = useRef(dragZoomAxes);
  dragZoomAxesRef.current = dragZoomAxes;
  const chartAreaRef = useRef(chartArea);
  chartAreaRef.current = chartArea;

  const [xDomain, setXDomain] = useState<Domain1D>(baseXDomain);
  const [yDomain, setYDomain] = useState<Domain1D>(baseYDomain);
  const [rectPreview, setRectPreview] = useState<RectPreview | null>(null);

  // Refs so the gesture handlers can read the freshest converters
  // without re-binding on every render.
  const xPxToDataRef = useRef(xScalePxToData);
  const yPxToDataRef = useRef(yScalePxToData);
  xPxToDataRef.current = xScalePxToData;
  yPxToDataRef.current = yScalePxToData;

  const reset = useCallback(() => {
    setXDomain(baseXDomain);
    setYDomain(baseYDomain);
    setRectPreview(null);
  }, [baseXDomain, baseYDomain]);

  // Reset to the new base when the data's underlying base domain changes
  // (e.g. user changes well selection → data extent shifts). Only resets
  // if the user wasn't already zoomed — preserves their view otherwise.
  const baseXKey = `${baseXDomain[0]}:${baseXDomain[1]}`;
  const baseYKey = `${baseYDomain[0]}:${baseYDomain[1]}`;
  useEffect(() => {
    setXDomain((current) => (nearlyEqual(current, xDomain) ? baseXDomain : current));
    setYDomain((current) => (nearlyEqual(current, yDomain) ? baseYDomain : current));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseXKey, baseYKey]);

  // Gesture set:
  //   - Drag rectangle      → zoom to that box
  //   - Shift + drag        → pan (cursor anchors to its data point)
  //   - Ctrl/Cmd + wheel    → zoom in/out centered on cursor
  //   - Double-click        → reset to base
  useEffect(() => {
    if (!rectangleZoom) return;
    const svg = svgRef.current;
    if (!svg) return;

    let dragStart: { x: number; y: number; mode: 'x' | 'y' | 'xy' } | null = null;
    // For Shift+pan / Ctrl+pan: the data-space point the cursor was over
    // when the drag began. We keep panning the domain so the cursor stays
    // over that same data point.
    let panAnchor: { x: number; y: number } | null = null;

    // Build the visible preview rectangle. For 'x' mode the rect spans full
    // chart height (signals "x-only zoom"). For 'y' mode it spans full
    // width. For 'xy' it's the user's exact drag rect.
    const buildRect = (
      startX: number,
      startY: number,
      curX: number,
      curY: number,
      mode: 'x' | 'y' | 'xy',
    ): RectPreview => {
      const area = chartAreaRef.current;
      const xLo = Math.min(startX, curX);
      const xHi = Math.max(startX, curX);
      const yLo = Math.min(startY, curY);
      const yHi = Math.max(startY, curY);
      if (mode === 'x' && area) {
        return { x0: xLo, x1: xHi, y0: area.top, y1: area.bottom };
      }
      if (mode === 'y' && area) {
        return { x0: area.left, x1: area.right, y0: yLo, y1: yHi };
      }
      return { x0: xLo, y0: yLo, x1: xHi, y1: yHi };
    };

    const onDown = (e: PointerEvent) => {
      // Only primary button.
      if (e.button !== 0) return;
      // Skip if a child element wants the event (e.g. draggable MCL line).
      if (e.defaultPrevented) return;
      const rect = svg.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // Ctrl (or Cmd on Mac) + drag = pan (anchored). Shift always means "free 2D zoom"
      // regardless of dragZoomAxes default.
      if (e.ctrlKey || e.metaKey) {
        panAnchor = {
          x: xPxToDataRef.current(x),
          y: yPxToDataRef.current(y),
        };
        return;
      }

      const mode: 'x' | 'y' | 'xy' = e.shiftKey ? 'xy' : dragZoomAxesRef.current;
      dragStart = { x, y, mode };
      setRectPreview(buildRect(x, y, x, y, mode));
    };

    const onMove = (e: PointerEvent) => {
      const rect = svg.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      if (panAnchor) {
        const currentDataX = xPxToDataRef.current(x);
        const currentDataY = yPxToDataRef.current(y);
        const dx = currentDataX - panAnchor.x;
        const dy = currentDataY - panAnchor.y;
        // Shift the domain so the original anchor lands back under the cursor.
        setXDomain((prev) => [prev[0] - dx, prev[1] - dx]);
        setYDomain((prev) => [prev[0] - dy, prev[1] - dy]);
        return;
      }

      if (!dragStart) return;
      setRectPreview(buildRect(dragStart.x, dragStart.y, x, y, dragStart.mode));
    };

    const onUp = () => {
      if (panAnchor) {
        panAnchor = null;
        return;
      }
      if (!dragStart) return;
      const startedAt = dragStart;
      dragStart = null;
      setRectPreview((cur) => {
        if (!cur) return null;
        const dx = cur.x1 - cur.x0;
        const dy = cur.y1 - cur.y0;
        // Per-mode min-drag enforcement: only the active axes need to
        // clear the threshold. A 200px horizontal sweep in 'x' mode is
        // valid even though the (fixed full-height) dy is huge — we don't
        // gate on that.
        const xOk = startedAt.mode === 'y' || dx >= minDragPx;
        const yOk = startedAt.mode === 'x' || dy >= minDragPx;
        if (!xOk || !yOk) {
          return null;
        }
        // Convert pixel rect → data domains. y-axis is inverted in SVG
        // (top = max data), so y0 px → max data, y1 px → min data.
        if (startedAt.mode !== 'y') {
          const x0Data = xPxToDataRef.current(cur.x0);
          const x1Data = xPxToDataRef.current(cur.x1);
          setXDomain([x0Data, x1Data]);
        }
        if (startedAt.mode !== 'x') {
          const yTopData = yPxToDataRef.current(cur.y0);
          const yBotData = yPxToDataRef.current(cur.y1);
          setYDomain([yBotData, yTopData]);
        }
        return null;
      });
    };

    // Ctrl/Cmd + wheel = zoom in/out, centered on the cursor. We require a
    // modifier so plain wheel scroll over a chart still scrolls the page
    // (Plotly does the same — page-scroll collisions with default wheel
    // zoom are infuriating).
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      e.preventDefault();
      const rect = svg.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const dataX = xPxToDataRef.current(px);
      const dataY = yPxToDataRef.current(py);
      const factor = e.deltaY > 0 ? 1.15 : 1 / 1.15;
      setXDomain((prev) => [
        dataX + (prev[0] - dataX) * factor,
        dataX + (prev[1] - dataX) * factor,
      ]);
      setYDomain((prev) => [
        dataY + (prev[0] - dataY) * factor,
        dataY + (prev[1] - dataY) * factor,
      ]);
    };

    svg.addEventListener('wheel', onWheel, { passive: false });
    svg.addEventListener('pointerdown', onDown);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      svg.removeEventListener('wheel', onWheel);
      svg.removeEventListener('pointerdown', onDown);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [svgRef, rectangleZoom, minDragPx]);

  // Double-click reset. Fires on the SVG; child elements that don't want
  // to reset (e.g. draggable MCL line) should call stopPropagation on
  // their own dblclick handlers.
  useEffect(() => {
    if (!dblclickResets) return;
    const svg = svgRef.current;
    if (!svg) return;
    const onDbl = (e: MouseEvent) => {
      if (e.defaultPrevented) return;
      reset();
    };
    svg.addEventListener('dblclick', onDbl);
    return () => svg.removeEventListener('dblclick', onDbl);
  }, [svgRef, dblclickResets, reset]);

  const isZoomed =
    !nearlyEqual(xDomain, baseXDomain) || !nearlyEqual(yDomain, baseYDomain);

  return {
    xDomain,
    yDomain,
    rectPreview,
    setXDomain,
    setYDomain,
    reset,
    isZoomed,
  };
}
